import { useEffect, useRef, useMemo } from "react";
import { Marker } from "react-leaflet";

/**
 * SmoothMarker — a Leaflet Marker that glides between lat/lng updates instead
 * of snapping. Designed for Traccar GPS pins that refresh at ~1 Hz.
 *
 * When `lat`/`lng` props change, animates the underlying Leaflet marker's
 * position from its current displayed coords to the new target over `duration`
 * ms using requestAnimationFrame + an ease-out curve. The React tree is NOT
 * re-rendered per frame — we mutate the Leaflet object directly via `ref`.
 *
 * IMPORTANT: the <Marker position> prop is pinned to the INITIAL mount coords
 * via useMemo so react-leaflet doesn't fight our rAF tween by calling
 * setLatLng internally on every parent re-render.
 *
 * Props:
 *   lat, lng   — target coordinates (required)
 *   duration   — tween length in ms (default 900 — slightly less than the
 *                1000 ms poll so the pin finishes moving just before the next
 *                update arrives, preventing visible stutter)
 *   icon       — Leaflet icon
 *   children   — <Popup> etc.
 */
export function SmoothMarker({ lat, lng, duration = 900, icon, children, linear = false, adaptive = false }) {
  const markerRef = useRef(null);
  const lastFixRef = useRef(null);
  const rafRef = useRef(null);
  const currentRef = useRef({ lat, lng });

  // Pin <Marker position> to the first mount's coords so react-leaflet never
  // auto-snaps the marker on parent re-render; we fully own position updates
  // via markerRef.current.setLatLng().
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialPosition = useMemo(() => [lat, lng], []);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const startLat = currentRef.current.lat ?? lat;
    const startLng = currentRef.current.lng ?? lng;
    const dLat = lat - startLat;
    const dLng = lng - startLng;
    if (Math.abs(dLat) < 1e-9 && Math.abs(dLng) < 1e-9) {
      currentRef.current = { lat, lng };
      marker.setLatLng([lat, lng]);
      return;
    }
    const startTs = performance.now();
    // Adaptive: glide over the real gap between fixes so the icon never stalls or leaps between updates.
    const gap = lastFixRef.current ? startTs - lastFixRef.current : duration;
    lastFixRef.current = startTs;
    const dur = adaptive ? Math.min(4000, Math.max(300, gap * 1.05)) : duration;
    const ease = linear ? (t) => t : (t) => 1 - Math.pow(1 - t, 3); // linear keeps 2s fixes gliding continuously
    const step = (now) => {
      const t = Math.min(1, (now - startTs) / dur);
      const k = ease(t);
      const curLat = startLat + dLat * k;
      const curLng = startLng + dLng * k;
      currentRef.current = { lat: curLat, lng: curLng };
      marker.setLatLng([curLat, curLng]);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [lat, lng, duration, linear, adaptive]);

  return (
    <Marker ref={markerRef} position={initialPosition} icon={icon}>
      {children}
    </Marker>
  );
}
